import { useEffect, useState, useContext, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { BASEMAPS, type BasemapKey } from '../../../constants/basemaps';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from '../../../utils/axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';

// Fix default marker icon (webpack asset issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

/* ── Types ─────────────────────────────────────────────────────────── */
interface IncidentReport {
  id: number;
  fullName: string | null;       // backend field name
  emailAddress: string | null;   // backend field name
  phoneNumber: string | null;    // backend field name
  location: string | null;
  crimeTypes: string | null;
  description: string | null;
  status: 'pending' | 'reviewed' | 'merged';
  adminNotes: string | null;
  mergedCaseId: number | null;
  createdAt: string | null;
}

interface MergeForm {
  caseNo: string;
  dateOfSeizure: string;
  nameOfAccused: string;
  nationality: string;
  gender: string;
  location: string;
  country: string;
  species: string;
  exhibit: string;
  weight: string;
  charge1: string;
  charge2: string;
  judgmentDate: string;
  judgmentOutcome: string;
  sentencing: string;
  caseStatus: string;
  latitude: string;
  longitude: string;
}

/* ── Helpers ───────────────────────────────────────────────────────── */
function parseCrimes(raw: string | null): string {
  if (!raw) return '—';
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return arr.join(', ');
  } catch { /* not JSON */ }
  return raw;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    pending:  'bg-amber-500/15 text-amber-400 border-amber-500/20',
    reviewed: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
    merged:   'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  };
  return map[status] ?? 'bg-white/8 text-white/35 border-white/10';
}

const EMPTY_FORM: MergeForm = {
  caseNo: '', dateOfSeizure: '', nameOfAccused: '', nationality: '',
  gender: '', location: '', country: '', species: '', exhibit: '',
  weight: '', charge1: '', charge2: '', judgmentDate: '',
  judgmentOutcome: '', sentencing: '', caseStatus: 'ongoing',
  latitude: '', longitude: '',
};

/* ── Map click listener (inside MapContainer) ──────────────────────── */
function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

/* ── MergeModal ────────────────────────────────────────────────────── */
function MergeModal({
  incident, token, onClose, onMerged,
}: {
  incident: IncidentReport;
  token: string;
  onClose: () => void;
  onMerged: (id: number, caseId: number) => void;
}) {
  const [basemap, setBasemap] = useState<BasemapKey>('dark');
  const [form, setForm] = useState<MergeForm>({
    ...EMPTY_FORM,
    location:      incident.location ?? '',
    nameOfAccused: incident.fullName ?? '',
    charge1:       parseCrimes(incident.crimeTypes),
    dateOfSeizure: incident.createdAt ? incident.createdAt.split('T')[0] : '',
  });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [showMap,  setShowMap]  = useState(false);

  const set = (k: keyof MergeForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleMapPick = useCallback((lat: number, lng: number) => {
    setForm((p) => ({
      ...p,
      latitude:  lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const res = await axios.post(
        `${REACT_APP_UPLOADS}/api/v1/incident-reports/${incident.id}/merge`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onMerged(incident.id, res.data?.id ?? 0);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to merge incident.');
    } finally {
      setSaving(false);
    }
  }

  const textField = (label: string, k: keyof MergeForm, type = 'text') => (
    <div>
      <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={form[k]}
        onChange={set(k)}
        className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
      />
    </div>
  );

  const markerPos: [number, number] | null =
    form.latitude && form.longitude
      ? [parseFloat(form.latitude), parseFloat(form.longitude)]
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full max-w-2xl rounded-2xl border border-white/8 overflow-hidden flex flex-col" style={{ background: '#071a0f', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/6 shrink-0">
          <p className="text-green-400 text-[10px] uppercase tracking-widest mb-0.5">Refine &amp; Merge</p>
          <h3 className="text-white/85 text-sm font-semibold">Incident #{incident.id} → New Case Record</h3>
        </div>

        {/* Source info strip */}
        <div className="px-6 py-3 border-b border-white/5 shrink-0" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-white/40">
            <span><span className="text-white/25">Reporter:</span> {incident.fullName ?? '—'}</span>
            <span><span className="text-white/25">Email:</span> {incident.emailAddress ?? '—'}</span>
            <span><span className="text-white/25">Crimes:</span> {parseCrimes(incident.crimeTypes)}</span>
            {incident.description && <span className="w-full"><span className="text-white/25">Description:</span> {incident.description}</span>}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
          <div className="grid grid-cols-2 gap-3">
            {textField('Case No.', 'caseNo')}
            {textField('Date of Seizure', 'dateOfSeizure', 'date')}
            {textField('Name of Accused', 'nameOfAccused')}
            {textField('Nationality', 'nationality')}
            {textField('Gender', 'gender')}
            {textField('Location', 'location')}
            {textField('Country', 'country')}
            {textField('Species', 'species')}
            {textField('Exhibit', 'exhibit')}
            {textField('Weight / Quantity', 'weight')}
            {textField('Charge 1', 'charge1')}
            {textField('Charge 2', 'charge2')}
            {textField('Judgment Date', 'judgmentDate', 'date')}
            {textField('Judgment Outcome', 'judgmentOutcome')}
            {textField('Sentencing', 'sentencing')}

            <div>
              <label htmlFor="merge-case-status" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">Case Status</label>
              <select
                id="merge-case-status"
                value={form.caseStatus}
                onChange={set('caseStatus')}
                style={{ colorScheme: 'dark' }}
                className="w-full bg-[#071a0f] border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 appearance-none"
              >
                <option value="ongoing">Ongoing</option>
                <option value="concluded">Concluded</option>
                <option value="absconded">Absconded</option>
              </select>
            </div>
          </div>

          {/* ── Coordinates section ─────────────────────────────────── */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/40 text-[10px] uppercase tracking-wider">Seizure Coordinates</p>
              <button
                type="button"
                onClick={() => setShowMap((v) => !v)}
                className={`text-[10px] px-3 py-1 rounded-lg border transition-colors ${
                  showMap
                    ? 'border-green-500/40 text-green-400 bg-green-500/10'
                    : 'border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
                }`}
              >
                {showMap ? '▲ Hide map' : '🗺 Pick on map'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="merge-latitude" className="block text-white/30 text-[9px] uppercase tracking-wider mb-1">Latitude</label>
                <input
                  id="merge-latitude"
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={set('latitude')}
                  placeholder="-1.286389"
                  className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
                />
              </div>
              <div>
                <label htmlFor="merge-longitude" className="block text-white/30 text-[9px] uppercase tracking-wider mb-1">Longitude</label>
                <input
                  id="merge-longitude"
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={set('longitude')}
                  placeholder="36.817223"
                  className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
                />
              </div>
            </div>

            {showMap && (
              <div className="mt-3 rounded-xl overflow-hidden border border-white/10 relative" style={{ height: 280 }}>
                <MapContainer
                  center={markerPos ?? [0, 20]}
                  zoom={markerPos ? 10 : 3}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl={false}
                >
                  <TileLayer
                    key={basemap}
                    url={BASEMAPS[basemap].url}
                    attribution={BASEMAPS[basemap].attribution}
                    maxZoom={BASEMAPS[basemap].maxZoom}
                  />
                  <MapClickHandler onPick={handleMapPick} />
                  {markerPos && <Marker position={markerPos} />}
                </MapContainer>
                {/* Basemap toggle */}
                <div className="absolute top-2 right-2 z-[1000] flex rounded-lg overflow-hidden border border-white/15" style={{ background: 'rgba(4,31,14,0.85)', backdropFilter: 'blur(8px)' }}>
                  {(['dark', 'street', 'satellite'] as BasemapKey[]).map((bm) => (
                    <button key={bm} onClick={() => setBasemap(bm)} title={BASEMAPS[bm].label}
                      className={`px-2 py-1 text-[9px] font-semibold transition-all ${basemap === bm ? 'bg-green-700/50 text-green-300' : 'text-white/35 hover:text-white/65'}`}>
                      {bm === 'dark' ? '🌑' : bm === 'street' ? '🗺' : '🛰'}
                    </button>
                  ))}
                </div>
                <p className="text-white/30 text-[9px] text-center py-1.5" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  Click anywhere on the map to set coordinates
                </p>
              </div>
            )}
          </div>

          {error && <p className="mt-3 text-red-400 text-xs">{error}</p>}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/6 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs text-white/50 hover:text-white/80 transition-colors">Cancel</button>
          <button
            onClick={submit as any}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Merging…' : 'Merge into Dataset'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main View ─────────────────────────────────────────────────────── */
type Tab = 'all' | 'pending' | 'reviewed' | 'merged';

export default function ReportedIncidentsView() {
  const { state } = useContext(AuthContext);
  const token = state.userToken ?? '';

  const [incidents, setIncidents]   = useState<IncidentReport[]>([]);
  const [loading,   setLoading]     = useState(true);
  const [tab,       setTab]         = useState<Tab>('all');
  const [expanded,  setExpanded]    = useState<number | null>(null);
  const [merging,   setMerging]     = useState<IncidentReport | null>(null);
  const [reviewing, setReviewing]   = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/incident-reports/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setIncidents(Array.isArray(res.data) ? res.data : []))
      .catch((_err) => { /* non-critical — user sees empty list */ })
      .finally(() => setLoading(false));
  }, [token]);

  async function markReviewed(id: number) {
    if (reviewing.has(id)) return;
    setReviewing((p) => new Set(Array.from(p).concat(id)));
    try {
      await axios.put(
        `${REACT_APP_UPLOADS}/api/v1/incident-reports/${id}/status`,
        { status: 'reviewed' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIncidents((prev) => prev.map((i) => i.id === id ? { ...i, status: 'reviewed' } : i));
    } catch { /* silent */ }
    finally { setReviewing((p) => { const s = new Set(p); s.delete(id); return s; }); }
  }

  function onMerged(id: number, caseId: number) {
    setIncidents((prev) => prev.map((i) => i.id === id ? { ...i, status: 'merged', mergedCaseId: caseId } : i));
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'all',      label: 'All' },
    { key: 'pending',  label: 'Pending' },
    { key: 'reviewed', label: 'Reviewed' },
    { key: 'merged',   label: 'Merged' },
  ];

  const filtered = tab === 'all' ? incidents : incidents.filter((i) => i.status === tab);

  const counts = {
    all:      incidents.length,
    pending:  incidents.filter((i) => i.status === 'pending').length,
    reviewed: incidents.filter((i) => i.status === 'reviewed').length,
    merged:   incidents.filter((i) => i.status === 'merged').length,
  };

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Reported Incidents</h2>
        <p className="text-white/30 text-xs mt-0.5">Review and merge public incident reports into the seizure case database</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total',    value: counts.all,      color: 'text-white' },
          { label: 'Pending',  value: counts.pending,  color: 'text-amber-400' },
          { label: 'Reviewed', value: counts.reviewed, color: 'text-violet-400' },
          { label: 'Merged',   value: counts.merged,   color: 'text-emerald-400' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-white/6 px-4 py-3" style={{ background: '#0a2e18' }}>
            <span className="text-white/30 text-[10px] uppercase tracking-wider">{s.label}</span>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-4 border-b border-white/6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-green-500 text-green-400'
                : 'border-transparent text-white/35 hover:text-white/60'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 text-[9px] tabular-nums ${tab === t.key ? 'text-green-400/70' : 'text-white/20'}`}>
              {counts[t.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Incident list */}
      <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <p className="text-white/20 text-sm">No {tab === 'all' ? '' : tab} incidents found</p>
          </div>
        ) : (
          <div>
            {filtered.map((incident) => {
              const isOpen = expanded === incident.id;
              return (
                <div key={incident.id} className="border-b border-white/4 last:border-0">
                  {/* Row */}
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/2 cursor-pointer transition-colors"
                    onClick={() => setExpanded(isOpen ? null : incident.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setExpanded(isOpen ? null : incident.id)}
                  >
                    <span className="text-white/20 text-[10px] tabular-nums w-8 shrink-0">#{incident.id}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/70 text-xs font-medium truncate">{incident.fullName ?? 'Anonymous'}</p>
                      <p className="text-white/30 text-[10px] truncate mt-0.5">{parseCrimes(incident.crimeTypes)}</p>
                    </div>
                    <span className="hidden sm:block text-white/35 text-[10px] truncate max-w-[120px]">{incident.location ?? '—'}</span>
                    <span className="hidden md:block text-white/25 text-[10px] whitespace-nowrap">
                      {incident.createdAt ? new Date(incident.createdAt).toLocaleDateString() : '—'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full border text-[9px] font-semibold whitespace-nowrap ${statusBadge(incident.status)}`}>
                      {incident.status}
                    </span>
                    <svg className={`w-3.5 h-3.5 text-white/25 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="border-t border-white/5 px-5 py-4" style={{ background: 'rgba(0,0,0,0.1)' }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        {[
                          ['Reporter', incident.fullName],
                          ['Email',    incident.emailAddress],
                          ['Phone',    incident.phoneNumber],
                          ['Location', incident.location],
                          ['Crime Types', parseCrimes(incident.crimeTypes)],
                          ['Submitted', incident.createdAt ? new Date(incident.createdAt).toLocaleString() : null],
                        ].map(([label, value]) => (
                          <div key={label as string}>
                            <p className="text-white/25 text-[9px] uppercase tracking-wider mb-0.5">{label}</p>
                            <p className="text-white/65 text-xs">{value ?? '—'}</p>
                          </div>
                        ))}
                        {incident.description && (
                          <div className="col-span-1 sm:col-span-2">
                            <p className="text-white/25 text-[9px] uppercase tracking-wider mb-0.5">Description</p>
                            <p className="text-white/60 text-xs leading-relaxed">{incident.description}</p>
                          </div>
                        )}
                        {incident.mergedCaseId && (
                          <div>
                            <p className="text-white/25 text-[9px] uppercase tracking-wider mb-0.5">Merged Case</p>
                            <p className="text-emerald-400 text-xs font-semibold">Case #{incident.mergedCaseId}</p>
                          </div>
                        )}
                      </div>

                      {incident.status !== 'merged' && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {incident.status === 'pending' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); markReviewed(incident.id); }}
                              disabled={reviewing.has(incident.id)}
                              className="px-3 py-1.5 rounded-lg border border-violet-500/30 text-violet-400 text-[10px] font-semibold hover:bg-violet-500/10 transition-colors disabled:opacity-40"
                            >
                              {reviewing.has(incident.id) ? 'Marking…' : 'Mark Reviewed'}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setMerging(incident); }}
                            className="px-3 py-1.5 rounded-lg bg-green-700 hover:bg-green-600 text-white text-[10px] font-semibold transition-colors"
                          >
                            Refine &amp; Merge
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Merge modal */}
      {merging && (
        <MergeModal
          incident={merging}
          token={token}
          onClose={() => setMerging(null)}
          onMerged={onMerged}
        />
      )}
    </div>
  );
}
